/**
 * Dimension readout formatting.
 *
 * READOUTS show architectural fractions: the value is expressed as 1/2, 1/4 or
 * 1/8 where it lands cleanly, and is otherwise rounded to the nearest 1/8".
 * EDITABLE FIELDS are deliberately NOT formatted this way — they seed and
 * accept plain decimals, so nothing here belongs in an editor's seed value.
 * See reference-docs/element-panels/overview.md.
 *
 * Two surfaces, one fraction rule:
 *
 *   formatInches      objects and fixtures    30"          14 1/2"
 *   formatFeetInches  walls, rooms, openings  12'-6 1/2"   10'-0"
 *
 * `metersToFeetInches` in floorPlanUtils is a THIRD thing and is not this:
 * it renders 1/16ths with padded spacing for SVG measurement labels on the
 * floor plan, and its output is pinned by tests. Leave it alone.
 */

import {
  EIGHTHS_PER_INCH,
  splitEighths,
  inchesText,
  formatInchesValue,
} from '@shared-lib/dimensioning/inchFractions';

export const METERS_TO_INCHES = 39.3701;

const EIGHTHS_PER_FOOT = EIGHTHS_PER_INCH * 12;

/** What every formatter returns for a value it cannot express. */
export const NO_VALUE = '—';

const isFormattable = (m) => typeof m === 'number' && Number.isFinite(m);

const eighthsOf = (meters) =>
  Math.round(Math.abs(meters) * METERS_TO_INCHES * EIGHTHS_PER_INCH);

/**
 * Plain inches, for objects and fixtures: `30"`, `14 1/2"`, `1/2"`.
 * A 30" vanity reads as 30", never as 2'-6".
 */
export const formatInches = (meters) =>
  isFormattable(meters)
    ? formatInchesValue(meters * METERS_TO_INCHES)
    : NO_VALUE;

/**
 * The same readout for a value that is ALREADY in inches. Catalog model
 * dimensions are stored in inches while scene geometry is in meters, and a
 * single readout can be fed by either — so both reach the formatter rather
 * than one of them being rounded by hand on the way. The fraction rule lives
 * in `@shared-lib/dimensioning/inchFractions`, so the books estimate's cabinet
 * lines, priced on the server, read widths the same way.
 */
export { formatInchesValue };

/**
 * Feet and inches, for structural measurements — walls, rooms, openings:
 * `12'-6 1/2"`, `10'-0"`. Under a foot it degrades to plain inches (`6 1/2"`)
 * rather than printing `0'-6 1/2"`.
 */
export const formatFeetInches = (meters) => {
  if (!isFormattable(meters)) return NO_VALUE;
  const sign = meters < 0 ? '-' : '';
  const totalEighths = eighthsOf(meters);
  const feet = Math.floor(totalEighths / EIGHTHS_PER_FOOT);
  const parts = splitEighths(totalEighths - feet * EIGHTHS_PER_FOOT);
  if (feet === 0) return `${sign}${inchesText(parts)}"`;
  return `${sign}${feet}'-${inchesText(parts)}"`;
};

/**
 * The EDITOR-side counterpart to `formatInches`, and deliberately unlike it.
 *
 * A field seeds and accepts PLAIN DECIMAL inches — `4.5`, not `4 1/2` — with
 * no unit suffix, because it is a value being typed rather than a value being
 * read. It is accurate rather than rounded: seeding a 35.5" door as `36` shows
 * the user a width their door does not have, and any apply that did fire would
 * write it.
 *
 * Returns '' for anything unformattable, since that is what an empty input is.
 */
export const inchesInputValue = (meters) => {
  if (!isFormattable(meters)) return '';
  // Snapped to the SAME 1/8" the readout uses, so the two agree: a door the
  // readout calls 36" seeds as `36`, not `35.98`. Scanned geometry is stored
  // in meters and is rarely exact, and a field full of measurement noise is
  // no more honest than a wrong number — it just looks broken. An eighth
  // needs three decimals (4.625), which is the price of round-tripping.
  const eighths = Math.round(meters * METERS_TO_INCHES * EIGHTHS_PER_INCH);
  return String(eighths / EIGHTHS_PER_INCH);
};
